import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Checkbox,
  message,
  Switch,
  Space,
  Card,
  Tag,
  Alert,
  Upload,
  Progress,
} from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  SearchOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import axios from "../api/axios";
import { notesColumnFilter } from "../components/notesColumnFilter";
import { getPlanningModeLabels } from "../utils/planningMode";

const COLORS = {
  primary: "#3FB6C2",
  text: "#1F2937",
};

const GEOCODE_BULK_ENDPOINT = "/geocoding/validate/bulk";

const BusesPage = () => {
  const [buses, setBuses] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBus, setEditingBus] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deleteAllLoading, setDeleteAllLoading] = useState(false);
  const [profileAssistantOpen, setProfileAssistantOpen] = useState(false);
  const [profileAssistantLoading, setProfileAssistantLoading] = useState(false);
  const [copyFleetLoading, setCopyFleetLoading] = useState(false);
  const [duplicateProfileOpen, setDuplicateProfileOpen] = useState(false);
  const [duplicateProfileLoading, setDuplicateProfileLoading] = useState(false);
  const [duplicateProfile, setDuplicateProfile] = useState(null);
  const [bulkChecking, setBulkChecking] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [bulkResultOpen, setBulkResultOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState({
    okCount: 0,
    failCount: 0,
    total: 0,
    results: [],
  });
  const [invalidBusIds, setInvalidBusIds] = useState(() => new Set());
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);

  const [form] = Form.useForm();
  const [profileAssistantForm] = Form.useForm();
  const [duplicateProfileForm] = Form.useForm();
  const customCostsEnabled = Form.useWatch("customCostsEnabled", form);
  const labels = useMemo(
    () => getPlanningModeLabels(user?.planningMode),
    [user?.planningMode]
  );
  const tenderMode = user?.planningMode === "TENDER";

  const fetchBuses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/buses");
      setBuses(res.data || []);
    } catch {
      message.error(`Fehler beim Laden der ${labels.vehiclePlural}.`);
    } finally {
      setLoading(false);
    }
  }, [labels.vehiclePlural]);

  const fetchUser = useCallback(async () => {
    try {
      const res = await axios.get("/users/me");
      setUser(res.data);
      localStorage.setItem("user", JSON.stringify(res.data));
      window.dispatchEvent(new Event("planning-mode-updated"));
    } catch {
      setUser(null);
    }
  }, []);

  const refreshData = useCallback(async () => {
    await Promise.all([fetchUser(), fetchBuses()]);
  }, [fetchUser, fetchBuses]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const handleSave = async (values) => {
    try {
      const costValues = values.customCostsEnabled
        ? {}
        : {
            customBusCostPerKm: null,
            customBusCostPerHour: null,
            customAttendantCostPerKm: null,
            customAttendantCostPerHour: null,
            customFixedCostPerTour: null,
          };
      if (editingBus) {
        await axios.put(`/buses/${editingBus.id}`, {
          ...editingBus,
          ...values,
          ...costValues,
          createCount: undefined,
        });
        message.success(`${labels.vehicleSingular} aktualisiert.`);
      } else {
        const payload = {
          ...values,
          ...costValues,
          createCount: tenderMode ? values.createCount : undefined,
        };
        const res = await axios.post("/buses", payload);
        const createdCount = res.data?.createdCount ?? 1;
        message.success(
          tenderMode && createdCount > 1
            ? `${createdCount} Fahrzeugprofile erstellt.`
            : `${labels.vehicleSingular} erstellt.`
        );
      }

      setModalOpen(false);
      setEditingBus(null);
      form.resetFields();
      refreshData();
    } catch (err) {
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Speichern fehlgeschlagen.";

      message.error(backendMessage);
    }
  };

  const handleToggleActive = async (bus) => {
    const updatedBus = { ...bus, aktiv: !bus.aktiv };

    try {
      await axios.put(`/buses/${bus.id}`, updatedBus);

      setBuses((prev) =>
        prev.map((b) => (b.id === bus.id ? { ...b, aktiv: !b.aktiv } : b))
      );

      message.success("Status geändert.");
    } catch {
      message.error("Konnte Status nicht ändern.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/buses/${id}`);
      message.success(`${labels.vehicleSingular} gelöscht.`);
      refreshData();
    } catch (err) {
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Löschen fehlgeschlagen.";
      message.error(backendMessage);
    }
  };

  const handleDeleteAll = async () => {
    setDeleteAllLoading(true);
    try {
      await axios.delete("/buses");
      message.success(labels.vehicleDeletedAll);
      setInvalidBusIds(new Set());
      setImportResult(null);
      setDeleteAllOpen(false);
      await refreshData();
    } catch {
      message.error(`Alle ${labels.vehiclePlural} konnten nicht gelöscht werden.`);
    } finally {
      setDeleteAllLoading(false);
    }
  };

  const handleBusImport = async ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append("file", file);

    setImporting(true);
    setImportResult(null);

    try {
      const res = await axios.post("/buses/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const createdCount = res.data?.createdCount ?? 0;
      const skippedCount = res.data?.skippedCount ?? 0;
      const rowErrors = res.data?.rowErrors || [];

      if (createdCount > 0) {
        message.success(`${createdCount} ${labels.vehiclePlural} importiert.`);
        setImportResult({
          type: "success",
          message: `${createdCount} ${labels.vehiclePlural} importiert.`,
        });
      } else {
        message.warning(
          `Die Datei wurde gelesen, aber es wurden keine ${labels.vehiclePlural} importiert.`
        );
        setImportResult({
          type: "warning",
          message:
            `Die Datei wurde gelesen, aber es wurden keine ${labels.vehiclePlural} importiert.`,
        });
      }

      if (skippedCount > 0 || rowErrors.length > 0) {
        setImportResult({
          type: createdCount > 0 ? "warning" : "error",
          message: `${createdCount} ${labels.vehiclePlural} importiert. ${Math.max(
            skippedCount,
            rowErrors.length
          )} Zeilen wurden übersprungen oder konnten nicht gespeichert werden.`,
          details: rowErrors,
        });

        Modal.warning({
          title: "Import mit Hinweisen",
          width: 720,
          content: (
            <div>
              <p>
                {createdCount} {labels.vehiclePlural} wurden importiert,{" "}
                {Math.max(skippedCount, rowErrors.length)} Zeilen wurden
                übersprungen oder konnten nicht gespeichert werden.
              </p>
              {rowErrors.length > 0 && (
                <ul style={{ paddingLeft: 18, maxHeight: 260, overflowY: "auto" }}>
                  {rowErrors.slice(0, 30).map((error, index) => (
                    <li key={`${error}-${index}`}>{error}</li>
                  ))}
                  {rowErrors.length > 30 && (
                    <li>Weitere {rowErrors.length - 30} Hinweise ausgeblendet.</li>
                  )}
                </ul>
              )}
            </div>
          ),
        });
      }

      await refreshData();
      onSuccess?.(res.data);
    } catch (err) {
      const data = err?.response?.data || {};
      const backendMessage =
        data.message || data.error || "Import fehlgeschlagen.";

      message.error(backendMessage);
      setImportResult({
        type: "error",
        message: backendMessage,
        parsedCount: data.parsedCount,
        freeSlots: data.freeSlots,
        details: data.rowErrors || [],
      });
      Modal.error({
        title: "Import nicht möglich",
        width: 680,
        content: (
          <div>
            <p>{backendMessage}</p>
            {data.parsedCount != null && data.freeSlots != null && (
              <p>
                Gefundene {labels.vehiclePlural}: {data.parsedCount}
                <br />
                Freie Plätze im aktuellen Tarif: {data.freeSlots}
              </p>
            )}
          </div>
        ),
      });
      onError?.(err);
    } finally {
      setImporting(false);
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleBusExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get("/buses/export", {
        responseType: "blob",
      });
      downloadBlob(res.data, "busse.xlsx");
      message.success(labels.vehicleDownload);
    } catch {
      message.error(labels.vehicleDownloadFailed);
    } finally {
      setExporting(false);
    }
  };

  const openProfileAssistant = () => {
    setProfileAssistantOpen(true);
    setTimeout(() => {
      profileAssistantForm.setFieldsValue({
        depotAdresse: "",
        seatOnlyCount: 1,
        seatOnlySeats: 8,
        wheelchairBusCount: 1,
        wheelchairBusSeats: 6,
        wheelchairBusWheelchairSeats: 2,
        wheelchairBusSeatsPerWheelchair: 2.0,
        electricWheelchairBusCount: 0,
        electricWheelchairBusSeats: 6,
        electricWheelchairBusWheelchairSeats: 1,
        electricWheelchairBusElectricWheelchairSeats: 1,
        electricWheelchairBusSeatsPerWheelchair: 2.0,
        electricWheelchairBusSeatsPerElectricWheelchair: 3.0,
      });
    });
  };

  const handleCreateDefaultProfiles = async (values) => {
    setProfileAssistantLoading(true);

    try {
      const res = await axios.post("/buses/tender/default-profiles", values);
      const createdCount = res.data?.createdCount ?? 0;
      message.success(`${createdCount} Fahrzeugprofile angelegt.`);
      setProfileAssistantOpen(false);
      await refreshData();
    } catch (err) {
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Standardprofile konnten nicht angelegt werden.";
      message.error(backendMessage);
    } finally {
      setProfileAssistantLoading(false);
    }
  };

  const handleCopyFleetProfiles = async () => {
    setCopyFleetLoading(true);

    try {
      const res = await axios.post("/buses/tender/copy-from-fleet");
      const createdCount = res.data?.createdCount ?? 0;
      message.success(`${createdCount} Fahrzeugprofile übernommen.`);
      await refreshData();
    } catch (err) {
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Eigenfuhrpark konnte nicht als Profile übernommen werden.";
      message.error(backendMessage);
    } finally {
      setCopyFleetLoading(false);
    }
  };

  const openDuplicateProfile = (record) => {
    setDuplicateProfile(record);
    setDuplicateProfileOpen(true);
    setTimeout(() => {
      duplicateProfileForm.setFieldsValue({ additionalCount: 1 });
    });
  };

  const handleDuplicateProfile = async (values) => {
    if (!duplicateProfile?.id) return;

    setDuplicateProfileLoading(true);
    try {
      const res = await axios.post(`/buses/${duplicateProfile.id}/duplicate`, {
        additionalCount: values.additionalCount,
      });
      const createdCount = res.data?.createdCount ?? 0;
      message.success(`${createdCount} zusätzliche Profile angelegt.`);
      setDuplicateProfileOpen(false);
      setDuplicateProfile(null);
      await refreshData();
    } catch (err) {
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Profile konnten nicht vervielfacht werden.";
      message.error(backendMessage);
    } finally {
      setDuplicateProfileLoading(false);
    }
  };

  const runBulkAddressCheck = async () => {
    if (!buses?.length) {
      message.info(`Keine ${labels.vehiclePlural} zum Prüfen vorhanden.`);
      return;
    }

    const items = buses.map((bus) => ({
      passengerId: bus.id,
      name: bus.name,
      field: "depotAdresse",
      address: bus.depotAdresse || bus.depot?.name || "",
    }));

    setBulkChecking(true);
    setBulkProgress({ done: 0, total: items.length });
    setBulkResult({
      okCount: 0,
      failCount: 0,
      total: items.length,
      results: [],
    });

    try {
      const res = await axios.post(GEOCODE_BULK_ENDPOINT, { items });

      const data = res.data || {};
      const results = Array.isArray(data.results) ? data.results : [];

      setBulkResult({
        okCount: data.okCount ?? 0,
        failCount: data.failCount ?? results.filter((r) => !r.ok).length,
        total: data.total ?? items.length,
        results,
      });

      setBulkProgress({ done: items.length, total: items.length });

      const bad = new Set();
      for (const r of results) {
        if (!r.ok && r.passengerId != null) {
          bad.add(r.passengerId);
        }
      }

      setInvalidBusIds(bad);

      if ((data.failCount ?? 0) === 0) {
        message.success(`Alle Adressen ok (${data.okCount ?? items.length}).`);
      } else {
        message.warning(
          `Adressprüfung: ${data.okCount ?? 0} ok, ${
            data.failCount ?? 0
          } fehlerhaft.`
        );
      }

      setBulkResultOpen(true);
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Bulk-Prüfung fehlgeschlagen.";
      message.error(msg);
    } finally {
      setBulkChecking(false);
    }
  };

  const normalizeDecimal = (val) => {
    if (val == null) return undefined;
    if (typeof val === "string") {
      const parsed = parseFloat(val.replace(",", "."));
      return isNaN(parsed) ? undefined : parsed;
    }
    return val;
  };

  const openEdit = (record) => {
    setEditingBus(record);
    setModalOpen(true);

    setTimeout(() => {
      form.resetFields();
      form.setFieldsValue({
        ...record,
        depotAdresse: record.depotAdresse || record.depot?.name || "",
        sitzplaetzeProRollstuhlfahrer: normalizeDecimal(
          record.sitzplaetzeProRollstuhlfahrer
        ),
        sitzplaetzeProERollstuhlfahrer: normalizeDecimal(
          record.sitzplaetzeProERollstuhlfahrer
        ),
        maxHerausforderndeProBus: record.maxHerausforderndeProBus || 1,
        notizen: record.notizen || "",
        customCostsEnabled: Boolean(record.customCostsEnabled),
      });
    });
  };

  const openCreate = () => {
    setEditingBus(null);
    setModalOpen(true);

    setTimeout(() => {
      form.resetFields();
      form.setFieldsValue({
        name: "",
        depotAdresse: "",
        sitzplaetze: 6,
        rollstuhlplaetze: 2,
        eRollstuhlplaetze: 0,
        sitzplaetzeProRollstuhlfahrer: 1,
        sitzplaetzeProERollstuhlfahrer: 1,
        maxHerausforderndeProBus: 1,
        aktiv: true,
        notizen: "",
        createCount: 1,
        customCostsEnabled: false,
      });
    });
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    confirm();
    setSearchedColumn("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Suche ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />

        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Suchen
        </Button>

        <Button
          onClick={() => handleReset(clearFilters, confirm)}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </div>
    ),

    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? COLORS.primary : undefined }} />
    ),

    onFilter: (value, record) => {
      const val = value.toLowerCase();

      if (dataIndex === "depotAdresse") {
        return (
          (record.depotAdresse &&
            record.depotAdresse.toLowerCase().includes(val)) ||
          (record.depot &&
            record.depot.name &&
            record.depot.name.toLowerCase().includes(val))
        );
      }

      return record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(val)
        : "";
    },

    onFilterDropdownVisibleChange: (visible) => {
      if (visible) setTimeout(() => searchInput.current?.select(), 100);
    },

    render: (text) =>
      searchedColumn === dataIndex ? (
        <span
          style={{
            backgroundColor: "#FFF7D6",
            color: COLORS.text,
            padding: "0 3px",
            borderRadius: 4,
          }}
        >
          {text}
        </span>
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: "Aktiv",
      dataIndex: "aktiv",
      width: 140,
      align: "center",
      filters: [
        { text: "Aktiv", value: true },
        { text: "Inaktiv", value: false },
      ],
      onFilter: (value, record) => record.aktiv === value,
      sorter: (a, b) => (a.aktiv === b.aktiv ? 0 : a.aktiv ? -1 : 1),
      render: (_, bus) => (
        <Switch
          checked={bus.aktiv}
          checkedChildren="Aktiv"
          unCheckedChildren="Inaktiv"
          onChange={() => handleToggleActive(bus)}
          style={{
            background: bus.aktiv ? "#52c41a" : "#ff4d4f",
            whiteSpace: "nowrap",
          }}
        />
      ),
      fixed: "left",
    },
    {
      title: "Name",
      dataIndex: "name",
      width: 160,
      sorter: (a, b) => (a.name || "").localeCompare(b.name || "", "de", { numeric: true, sensitivity: "base" }),
      defaultSortOrder: "ascend",
      ...getColumnSearchProps("name"),
      fixed: "left",
    },
    {
      title: "Depot-Adresse",
      dataIndex: "depotAdresse",
      width: 260,
      sorter: (a, b) =>
        (a.depotAdresse || a.depot?.name || "").localeCompare(
          b.depotAdresse || b.depot?.name || ""
        ),
      render: (_, rec) => rec.depotAdresse || rec.depot?.name || "-",
      ...getColumnSearchProps("depotAdresse"),
    },
    {
      title: "Sitzplätze",
      dataIndex: "sitzplaetze",
      align: "center",
      sorter: (a, b) => (a.sitzplaetze ?? 0) - (b.sitzplaetze ?? 0),
      width: 130,
    },
    {
      title: "Rollstuhlplätze",
      dataIndex: "rollstuhlplaetze",
      align: "center",
      sorter: (a, b) =>
        (a.rollstuhlplaetze ?? 0) - (b.rollstuhlplaetze ?? 0),
      width: 165,
    },
    {
      title: "E-Rollstuhlplätze",
      dataIndex: "eRollstuhlplaetze",
      align: "center",
      sorter: (a, b) =>
        (a.eRollstuhlplaetze ?? 0) - (b.eRollstuhlplaetze ?? 0),
      width: 185,
    },
    {
      title: "Max. herausfordernde",
      dataIndex: "maxHerausforderndeProBus",
      align: "center",
      sorter: (a, b) =>
        (a.maxHerausforderndeProBus ?? 1) -
        (b.maxHerausforderndeProBus ?? 1),
      width: 195,
    },
    {
      title: "Kosten",
      dataIndex: "customCostsEnabled",
      width: 130,
      align: "center",
      filters: [
        { text: "Standard", value: false },
        { text: "Individuell", value: true },
      ],
      onFilter: (value, record) => Boolean(record.customCostsEnabled) === value,
      render: (enabled) => (
        <Tag color={enabled ? "blue" : "default"}>
          {enabled ? "Individuell" : "Standard"}
        </Tag>
      ),
    },
    {
      title: "Notizen",
      dataIndex: "notizen",
      width: 240,
      ...notesColumnFilter,
    },
    {
      title: "Aktionen",
      key: "action",
      width: 220,
      align: "left",
      fixed: "right",
      render: (_, record) => (
        <Space size={6} wrap>
          <Button size="small" onClick={() => openEdit(record)}>
            Bearbeiten
          </Button>

          {tenderMode && (
            <Button size="small" onClick={() => openDuplicateProfile(record)}>
              Anzahl erhöhen
            </Button>
          )}

          <Button size="small" danger onClick={() => handleDelete(record.id)}>
            Löschen
          </Button>
        </Space>
      ),
    },
  ];

  const bulkFailures = (bulkResult.results || []).filter((r) => !r.ok);

  const bulkFailureColumns = [
    { title: labels.vehicleSingular, dataIndex: "name", width: 160 },
    {
      title: "Feld",
      dataIndex: "field",
      width: 140,
      render: (v) => (v === "depotAdresse" ? labels.depotLabel : v),
    },
    { title: "Adresse", dataIndex: "address", width: 320 },
    { title: "Fehler", dataIndex: "error", width: 260 },
  ];

  const progressPercent =
    bulkProgress.total > 0
      ? Math.round((bulkProgress.done / bulkProgress.total) * 100)
      : 0;

  return (
    <div className="buses-page" style={{ maxWidth: "100%", overflowX: "auto" }}>
      <h2 className="app-page-title">{labels.vehiclePlural}</h2>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Maximal 9 Fahrgäste je Bus und Tour"
        description="Pro Wochentag und Fahrtrichtung können höchstens 9 Fahrgäste in einem Bus eingeplant werden. Je nach Sitzplätzen, Rollstuhlplätzen und benötigten Begleitpersonen kann die tatsächliche Kapazität niedriger sein."
      />

      <Card
        size="small"
        style={{
          marginBottom: 16,
          borderColor: "#DCE7F3",
          background: "#F4FBFC",
        }}
      >
        <Space wrap size="middle">
          <strong style={{ color: COLORS.text }}>
            {labels.countLabel}
          </strong>

          <Tag color="cyan" style={{ fontSize: 14 }}>
            {buses.length}
          </Tag>
        </Space>
      </Card>

      <Space style={{ marginBottom: 16 }} wrap>
        <Button type="primary" onClick={openCreate}>
          {labels.vehicleNew}
        </Button>

        {tenderMode && (
          <Button onClick={openProfileAssistant}>
            Standardprofile anlegen
          </Button>
        )}

        {tenderMode && (
          <Button onClick={handleCopyFleetProfiles} loading={copyFleetLoading}>
            Aus Eigenfuhrpark übernehmen
          </Button>
        )}

        <Upload
          accept=".xlsx,.xls,.numbers"
          showUploadList={false}
          customRequest={handleBusImport}
          disabled={importing}
        >
          <Button icon={<UploadOutlined />} loading={importing}>
            Excel/Numbers importieren
          </Button>
        </Upload>

        <Button
          icon={<DownloadOutlined />}
          onClick={handleBusExport}
          loading={exporting}
          disabled={!buses.length || loading || importing}
        >
          Excel herunterladen
        </Button>

        <Button
          onClick={runBulkAddressCheck}
          loading={bulkChecking}
          disabled={!buses.length || loading || importing}
        >
          Alle Adressen prüfen
        </Button>

        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={() => setDeleteAllOpen(true)}
          disabled={
            !buses.length ||
            loading ||
            importing ||
            bulkChecking ||
            deleteAllLoading
          }
        >
          {labels.vehicleDeleteAll}
        </Button>

        {bulkChecking && (
          <div style={{ minWidth: 260 }}>
            <Progress percent={progressPercent} />
          </div>
        )}

        {invalidBusIds.size > 0 && (
          <Tag color="red">
            {invalidBusIds.size} {labels.vehiclePlural} mit Adressfehlern markiert
          </Tag>
        )}
      </Space>

      {importResult && (
        <Alert
          type={importResult.type}
          showIcon
          closable
          onClose={() => setImportResult(null)}
          style={{ marginBottom: 16 }}
          message={importResult.message}
          description={
            <div>
              {importResult.parsedCount != null &&
                importResult.freeSlots != null && (
                  <div>
                    Gefundene {labels.vehiclePlural}: {importResult.parsedCount}
                    <br />
                    Freie Plätze im aktuellen Tarif: {importResult.freeSlots}
                  </div>
                )}

              {importResult.details?.length > 0 && (
                <ul style={{ paddingLeft: 18, marginTop: 8, marginBottom: 0 }}>
                  {importResult.details.slice(0, 8).map((detail, index) => (
                    <li key={`${detail}-${index}`}>{detail}</li>
                  ))}
                  {importResult.details.length > 8 && (
                    <li>
                      Weitere {importResult.details.length - 8} Hinweise
                      ausgeblendet.
                    </li>
                  )}
                </ul>
              )}
            </div>
          }
        />
      )}

      <Table
        dataSource={buses}
        columns={columns}
        rowKey="id"
        loading={loading}
        bordered
        pagination={{
          defaultPageSize: 12,
          showSizeChanger: true,
          pageSizeOptions: ["10", "12", "20", "50", "100"],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} von ${total} ${labels.tableTotal}`,
        }}
        scroll={{ x: 1915, y: 500 }}
        rowClassName={(record) =>
          invalidBusIds.has(record.id) ? "bus-row bus-row-invalid" : "bus-row"
        }
      />

      <Modal
        open={deleteAllOpen}
        title={`${labels.vehicleDeleteAll}?`}
        okText="Alle löschen"
        cancelText="Abbrechen"
        okButtonProps={{ danger: true }}
        confirmLoading={deleteAllLoading}
        onOk={handleDeleteAll}
        onCancel={() => setDeleteAllOpen(false)}
      >
        <p>Diese Aktion löscht alle {labels.vehiclePlural} deines Kontos dauerhaft.</p>
      </Modal>

      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        title={editingBus ? `${labels.vehicleSingular} bearbeiten` : labels.vehicleNew}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          scrollToFirstError
        >
          <Form.Item
            name="name"
            label={labels.vehicleName}
            rules={[{ required: true, message: "Bitte Namen angeben!" }]}
          >
            <Input maxLength={50} />
          </Form.Item>

          {tenderMode && !editingBus && (
            <Form.Item
              name="createCount"
              label="Anzahl Profile"
              rules={[
                { required: true, message: "Bitte Anzahl angeben." },
                {
                  type: "number",
                  min: 1,
                  message: "Bitte mindestens 1 angeben.",
                },
              ]}
            >
              <InputNumber min={1} max={99} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          )}

          <Form.Item
            name="depotAdresse"
            label={labels.depotLabel}
            rules={[
              { required: true, message: `Bitte ${labels.depotLabel} eingeben.` },
              { min: 5, message: `${labels.depotLabel} ist zu kurz.` },
            ]}
          >
            <Input placeholder="z. B. Depotstraße 1, 70173 Stuttgart" />
          </Form.Item>

          <Form.Item
            name="sitzplaetze"
            label={tenderMode ? "Gesamtkapazität" : "Sitzplätze"}
            rules={[
              { required: true },
              {
                type: "number",
                max: 9,
                message: "Es sind maximal 9 Sitzplätze zulässig.",
              },
            ]}
          >
            <InputNumber min={1} max={9} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="rollstuhlplaetze"
            label="Rollstuhlplätze"
            rules={[{ required: true }]}
          >
            <InputNumber min={0} max={9} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="eRollstuhlplaetze"
            label="E-Rollstuhlplätze"
            rules={[{ required: true }]}
          >
            <InputNumber min={0} max={9} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="sitzplaetzeProRollstuhlfahrer"
            label="Sitzplätze je Rollstuhl"
            rules={[{ required: true }]}
          >
            <InputNumber
              min={0}
              max={9}
              step={0.1}
              precision={1} decimalSeparator=","
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item
            name="sitzplaetzeProERollstuhlfahrer"
            label="Sitzplätze je E-Rollstuhl"
            rules={[{ required: true }]}
          >
            <InputNumber
              min={0}
              max={9}
              step={0.1}
              precision={1} decimalSeparator=","
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item
            name="maxHerausforderndeProBus"
            label="Max. Fahrgäste mit herausforderndem Verhalten"
            rules={[
              { required: true, message: "Bitte Maximalzahl angeben." },
              {
                type: "number",
                min: 1,
                message: "Bitte mindestens 1 angeben.",
              },
            ]}
          >
            <InputNumber min={1} max={99} precision={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="aktiv"
            valuePropName="checked"
            label="Aktiv"
            style={{ marginBottom: 10 }}
          >
            <Checkbox>{labels.activeLabel}</Checkbox>
          </Form.Item>

          <Form.Item
            name="customCostsEnabled"
            valuePropName="checked"
            label="Kosten"
            style={{ marginBottom: 10 }}
          >
            <Checkbox>Individuelle Kosten für dieses Fahrzeug verwenden</Checkbox>
          </Form.Item>

          {customCostsEnabled && (
            <Card size="small" title="Individuelle Fahrzeugkosten" style={{ marginBottom: 16 }}>
              <p style={{ marginTop: 0 }}>
                Diese Werte ersetzen für dieses Fahrzeug die benutzerweiten Kosten.
              </p>
              <Form.Item name="customBusCostPerKm" label="Buskosten pro Kilometer (€/km)" rules={[{ required: true }]}>
                <InputNumber min={0} step={0.01} precision={2} decimalSeparator="," style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item name="customBusCostPerHour" label="Buskosten pro Stunde (€/h)" rules={[{ required: true }]}>
                <InputNumber min={0} step={0.01} precision={2} decimalSeparator="," style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item name="customAttendantCostPerKm" label="Beifahrerkosten pro Kilometer (€/km)" rules={[{ required: true }]}>
                <InputNumber min={0} step={0.01} precision={2} decimalSeparator="," style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item name="customAttendantCostPerHour" label="Beifahrerkosten pro Stunde (€/h)" rules={[{ required: true }]}>
                <InputNumber min={0} step={0.01} precision={2} decimalSeparator="," style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item name="customFixedCostPerTour" label="Fixkosten pro Tour (€/Tour)" rules={[{ required: true }]}>
                <InputNumber min={0} step={0.01} precision={2} decimalSeparator="," style={{ width: "100%" }} />
              </Form.Item>
            </Card>
          )}

          <Form.Item name="notizen" label="Notizen">
            <Input.TextArea
              maxLength={1000}
              autoSize={{ minRows: 3, maxRows: 6 }}
              placeholder={labels.notePlaceholder}
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Speichern
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={profileAssistantOpen}
        onCancel={() => setProfileAssistantOpen(false)}
        title="Standardprofile anlegen"
        footer={null}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Hier stellst du bis zu drei Fahrzeugtypen für eine Ausschreibung zusammen."
          description="Für jeden Typ gibst du an, wie viele Profile angelegt werden sollen und welche Kapazität diese Profile haben."
        />

        <Form
          form={profileAssistantForm}
          layout="vertical"
          onFinish={handleCreateDefaultProfiles}
        >
          <Form.Item
            name="depotAdresse"
            label="Depot-/Standortannahme"
            rules={[
              { required: true, message: "Bitte Standortannahme eingeben." },
              { min: 5, message: "Standortannahme ist zu kurz." },
            ]}
            extra="Ein angenommener Start-/Endstandort für diese Profile, z. B. ein Landkreisbereich, ein Betriebshof oder eine Angebotsregion."
          >
            <Input placeholder="z. B. Landkreis Nord, Musterstraße 1" />
          </Form.Item>

          <Form.Item
            label="1. Fahrzeugtyp ohne Rollstuhlplatz"
            className="profile-type-group"
          >
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              <Form.Item
                name="seatOnlyCount"
                label="Anzahl Profile"
                rules={[{ required: true }]}
                style={{ marginBottom: 8 }}
              >
                <InputNumber min={0} max={99} precision={0} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                name="seatOnlySeats"
                label="Gesamtkapazität je Profil"
                rules={[{ required: true }]}
                style={{ marginBottom: 16 }}
              >
                <InputNumber min={1} max={9} precision={0} style={{ width: "100%" }} />
              </Form.Item>
            </Space>
          </Form.Item>

          <Form.Item
            label="2. Fahrzeugtyp mit Rollstuhlplätzen"
            className="profile-type-group"
          >
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              <Form.Item
                name="wheelchairBusCount"
                label="Anzahl Profile"
                rules={[{ required: true }]}
                style={{ marginBottom: 8 }}
              >
                <InputNumber min={0} max={99} precision={0} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                name="wheelchairBusSeats"
                label="Gesamtkapazität je Profil"
                rules={[{ required: true }]}
                style={{ marginBottom: 8 }}
              >
                <InputNumber min={1} max={9} precision={0} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                name="wheelchairBusWheelchairSeats"
                label="Maximale Anzahl Rollstuhlplätze"
                rules={[{ required: true }]}
                style={{ marginBottom: 8 }}
              >
                <InputNumber min={0} max={9} precision={0} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                name="wheelchairBusSeatsPerWheelchair"
                label="Sitzplätze je Rollstuhl"
                rules={[{ required: true }]}
                style={{ marginBottom: 16 }}
              >
                <InputNumber min={0.1} max={99} step={0.1} precision={1} decimalSeparator="," style={{ width: "100%" }} />
              </Form.Item>
            </Space>
          </Form.Item>

          <Form.Item
            label="3. Fahrzeugtyp mit E-Rollstuhlplätzen"
            className="profile-type-group"
          >
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              <Form.Item
                name="electricWheelchairBusCount"
                label="Anzahl Profile"
                rules={[{ required: true }]}
                style={{ marginBottom: 8 }}
              >
                <InputNumber min={0} max={99} precision={0} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                name="electricWheelchairBusSeats"
                label="Gesamtkapazität je Profil"
                rules={[{ required: true }]}
                style={{ marginBottom: 8 }}
              >
                <InputNumber min={1} max={9} precision={0} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                name="electricWheelchairBusWheelchairSeats"
                label="Maximale Anzahl Rollstuhlplätze"
                rules={[{ required: true }]}
                style={{ marginBottom: 8 }}
              >
                <InputNumber min={0} max={9} precision={0} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                name="electricWheelchairBusElectricWheelchairSeats"
                label="Maximale Anzahl E-Rollstuhlplätze"
                rules={[{ required: true }]}
                style={{ marginBottom: 8 }}
              >
                <InputNumber min={0} max={9} precision={0} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                name="electricWheelchairBusSeatsPerWheelchair"
                label="Sitzplätze je Rollstuhl"
                rules={[{ required: true }]}
                style={{ marginBottom: 8 }}
              >
                <InputNumber min={0.1} max={99} step={0.1} precision={1} decimalSeparator="," style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                name="electricWheelchairBusSeatsPerElectricWheelchair"
                label="Sitzplätze je E-Rollstuhl"
                rules={[{ required: true }]}
                style={{ marginBottom: 16 }}
              >
                <InputNumber min={0.1} max={99} step={0.1} precision={1} decimalSeparator="," style={{ width: "100%" }} />
              </Form.Item>
            </Space>
          </Form.Item>

          <Alert
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
            message="Beispiel: 1, 1, 0 legt je ein Profil aus Typ 1 und Typ 2 an."
            description="Gesamtkapazität ist die rechnerische Gesamtgrenze. Die maximalen Rollstuhlplätze begrenzen die Stellplätze; Sitzplätze je Rollstuhl/E-Rollstuhl legen fest, wie viel Kapazität ein solcher Platz verbraucht."
          />

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={profileAssistantLoading}
            >
              Profile anlegen
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={duplicateProfileOpen}
        onCancel={() => {
          setDuplicateProfileOpen(false);
          setDuplicateProfile(null);
        }}
        title="Anzahl für Profil erhöhen"
        footer={null}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={duplicateProfile?.name || "Fahrzeugprofil"}
          description="Gib an, wie viele zusätzliche identische Profile angelegt werden sollen."
        />

        <Form
          form={duplicateProfileForm}
          layout="vertical"
          onFinish={handleDuplicateProfile}
        >
          <Form.Item
            name="additionalCount"
            label="Zusätzliche Profile"
            rules={[
              { required: true, message: "Bitte Anzahl angeben." },
              {
                type: "number",
                min: 1,
                message: "Bitte mindestens 1 angeben.",
              },
            ]}
          >
            <InputNumber min={1} max={99} precision={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={duplicateProfileLoading}
            >
              Zusätzliche Profile anlegen
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={bulkResultOpen}
        onCancel={() => setBulkResultOpen(false)}
        title="Adressprüfung – Ergebnis"
        footer={[
          <Button key="close" onClick={() => setBulkResultOpen(false)}>
            Schließen
          </Button>,
        ]}
        width={980}
        destroyOnClose
      >
        <Alert
          type={bulkResult.failCount === 0 ? "success" : "warning"}
          showIcon
          message={
            bulkResult.failCount === 0
              ? `Alles ok – ${bulkResult.okCount} Prüfungen erfolgreich.`
              : `Es gibt fehlerhafte Adressen: ${bulkResult.okCount} ok, ${bulkResult.failCount} fehlerhaft.`
          }
          style={{ marginBottom: 12 }}
        />

        {bulkResult.failCount > 0 ? (
          <Table
            dataSource={bulkFailures}
            columns={bulkFailureColumns}
            rowKey={(r) => `${r.passengerId}-${r.field}-${r.address}`}
            pagination={{ pageSize: 8 }}
            bordered
            scroll={{ x: 900 }}
          />
        ) : (
          <div>Keine fehlerhaften Adressen gefunden.</div>
        )}
      </Modal>

      <style>{`
        .buses-page h2 {
          margin-top: 0;
          margin-bottom: 18px;
          color: #1F2937;
          font-weight: 700;
        }

        .buses-page .ant-table-thead > tr > th {
          white-space: nowrap;
          background: #F4FBFC !important;
          border-bottom: 1px solid #DCE7F3 !important;
          color: #1F2937 !important;
          font-weight: 700 !important;
        }

        .bus-row td {
          white-space: normal !important;
          word-break: break-word;
          line-height: 1.4;
          vertical-align: top;
          padding-top: 12px !important;
          padding-bottom: 12px !important;
        }

        .bus-row-invalid td {
          background: #fff1f0 !important;
        }

        .bus-row-invalid:hover td {
          background: #ffccc7 !important;
        }

        .profile-type-group {
          border: 1px solid #DCE7F3;
          border-radius: 8px;
          padding: 12px 12px 0;
          background: #F8FCFD;
          margin-bottom: 14px;
        }
      `}</style>
    </div>
  );
};

export default BusesPage;
